import { getClosestNode } from '@/lib/tiptap-advanced-utils';
import type { TableKitOptions } from '@tiptap/extension-table';
import type { ResolvedPos } from '@tiptap/pm/model';
import { Extension, type CommandProps } from '@tiptap/react';
import { TableHandleExtension } from '../../tiptap-node/table-node/extensions/table-handle';
import { TableNode } from '../../tiptap-node/table-node/extensions/table-node-extension';
import { NodeAlignment } from '../node-alignment-extension';
import { NodeBackground } from '../node-background-extension';
import { convertTableHeadersToCells } from './utils';
import { ZyTableCell, ZyTableCellHeadless } from './zytablecell-extension';
import { ZyTableHeader, ZyTableHeaderHeadless } from './zytableheader-extension';
import { ZyTableRow } from './zytablerow-extension';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyTable: {
            toggleDatabaseTable: () => ReturnType;
            insertGridLayout: (options: { rows: number; cols: number }) => ReturnType;
        };
    }
}

function parseIsLayoutGrid(element: HTMLElement): boolean {
    const classAttr = element.getAttribute('class') || '';
    if (classAttr.includes('layoutGrid')) {
        return true;
    }
    return element.parentElement?.classList.contains('layout-grid') ?? false;
}

function isInsideLayoutGrid($pos: ResolvedPos): boolean {
    for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === 'table' && node.attrs.isLayoutGrid) {
            return true;
        }
    }
    return false;
}

export const ZyTable = TableNode.extend({
    allowGapCursor: false,

    renderHTML({ node, HTMLAttributes }) {
        const html = this.parent?.({ node, HTMLAttributes });
        if (node.attrs.isLayoutGrid) {
            return ['div', { class: 'layout-grid table--layout-grid' }, html];
        }
        const wrapperClass = node.attrs.isDatabaseTable ? 'table table--database' : 'table';
        return ['div', { class: wrapperClass }, html];
    },

    addAttributes() {
        return {
            ...this.parent?.(),
            isDatabaseTable: {
                default: false,
                parseHTML: element => {
                    const classAttr = element.getAttribute('class') || '';
                    return classAttr.includes('dbTable');
                },
                renderHTML: attributes => {
                    if (attributes.isDatabaseTable) {
                        return { class: 'dbTable' };
                    }
                    return {};
                },
            },
            isLayoutGrid: {
                default: false,
                parseHTML: element => parseIsLayoutGrid(element as HTMLElement),
                renderHTML: attributes => {
                    if (attributes.isLayoutGrid) {
                        return { class: 'layoutGrid' };
                    }
                    return {};
                },
            },
        };
    },

    addCommands() {
        return {
            ...this.parent?.(),
            insertGridLayout:
                ({ rows, cols }) =>
                ({ chain, state }) => {
                    if (isInsideLayoutGrid(state.selection.$from)) {
                        return false;
                    }
                    return chain()
                        .focus()
                        .insertTable({ rows, cols, withHeaderRow: false })
                        .command(({ tr, dispatch, state }) => {
                            if (!dispatch) {
                                return true;
                            }

                            const $anchor = tr.selection.$anchor;
                            for (let depth = $anchor.depth; depth > 0; depth--) {
                                if ($anchor.node(depth).type.name === 'table') {
                                    const tablePos = $anchor.before(depth);
                                    tr.setNodeAttribute(tablePos, 'isLayoutGrid', true);
                                    tr.setNodeAttribute(tablePos, 'isDatabaseTable', false);
                                    const tableNode = tr.doc.nodeAt(tablePos);
                                    if (tableNode) {
                                        convertTableHeadersToCells(tr, tablePos, tableNode, state.schema);
                                    }
                                    return true;
                                }
                            }

                            return false;
                        })
                        .run();
                },
            toggleDatabaseTable:
                () =>
                ({ dispatch, editor, state }: CommandProps) => {
                    if (!dispatch) {
                        return false;
                    }
                    const tableNode = getClosestNode(editor, { nodeName: 'table' });
                    if (!tableNode) {
                        return false;
                    }
                    const tr = state.tr;
                    const nextIsDatabaseTable = !tableNode.node.attrs.isDatabaseTable;
                    tr.setNodeAttribute(tableNode.pos, 'isDatabaseTable', nextIsDatabaseTable);
                    if (nextIsDatabaseTable) {
                        tr.setNodeAttribute(tableNode.pos, 'isLayoutGrid', false);
                    }
                    dispatch(tr);
                    return true;
                },
        };
    },
});

export const ZyTableKitHeadless = Extension.create<TableKitOptions>({
    name: 'zyTableKit',

    addExtensions() {
        const extensions = [];

        if (this.options.table !== false) {
            extensions.push(ZyTable.configure(this.options.table));
        }

        if (this.options.tableCell !== false) {
            extensions.push(ZyTableCellHeadless.configure(this.options.tableCell));
        }

        if (this.options.tableHeader !== false) {
            extensions.push(ZyTableHeaderHeadless.extend().configure(this.options.tableHeader));
        }

        if (this.options.tableRow !== false) {
            extensions.push(ZyTableRow.configure(this.options.tableRow));
        }

        extensions.push(TableHandleExtension, NodeAlignment, NodeBackground);

        return extensions;
    },
});

export const ZyTableKit = ZyTableKitHeadless.extend({
    addExtensions() {
        const extensions = [];

        if (this.options.table !== false) {
            extensions.push(ZyTable.configure(this.options.table));
        }

        if (this.options.tableCell !== false) {
            extensions.push(ZyTableCell.configure(this.options.tableCell));
        }

        if (this.options.tableHeader !== false) {
            extensions.push(ZyTableHeader.extend().configure(this.options.tableHeader));
        }

        if (this.options.tableRow !== false) {
            extensions.push(ZyTableRow.configure(this.options.tableRow));
        }

        extensions.push(TableHandleExtension, NodeAlignment, NodeBackground);

        return extensions;
    },
});
